import { gql, useSubscription } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Dialog } from '@headlessui/react';
import { Auth } from 'aws-amplify';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { pathURL } from 'src/common/constants';
import { DataUser, PrimaryStore, QuotationListType, SalesRepresentative, SecondaryStore } from 'src/common/interfaces';
import { getFilEstimaPointList, getLogMaintenance, getMstUser } from 'src/graphql/queries';
import { insertedLogMaintenance } from 'src/graphql/subscriptions';
import { useAppLazyQuery } from 'src/libs/hooks';
import testEnv from 'src/tests/test-env';
import ModalGuidePrimaryStore from '../guide-primary-store/guide-primary-store';
import ModalGuideSecondaryStore from '../guide-secondary-store/guide-secondary-store';
import ModalGuideUser from '../guide-user/guide-user';
import MainLayout from '../main-layout';
import Maintenance from '../maintenance';

const defaultPrimaryStore = {
  mps_primary_cd: '',
  mps_primary_nm: '',
  error: {
    isErrorPrimaryCd: false,
  },
};

const defaultSecondaryStore = {
  mss_primary_cd: '',
  mss_secondary_cd: '',
  mss_secondary_nm: '',
};

const defaultSalesRepresentative = {
  id: undefined,
  name: undefined,
};

interface SearchInput {
  esp_est_date: {
    from: string | null;
    to: string | null;
  };
  esp_est_nm: string;
  esp_estima_full_no: {
    from: {
      child: number;
      parent: number;
    };
    to: {
      child: number;
      parent: number;
    };
  };
  esp_primary_cd: string | undefined;
  esp_secondary_cd: string | undefined;
  esp_syain_cd: number | undefined;
}

const QuotationList = () => {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const location = useLocation();
  const header = ['見積No.', '見積名', '見積日付', '一次店', '二次店'];
  const [quotationList, setQuotationList] = useState<Array<QuotationListType>>([]);
  const [searchQuotationList, { loading: isQuotationListLoading }] = useAppLazyQuery({
    query: gql(getFilEstimaPointList),
  });
  const [searchMstUser, { loading: isMstUserLoading }] = useAppLazyQuery({ query: gql(getMstUser) });

  const navigate = useNavigate();
  const [quoteName, setQuoteName] = useState<string>('');
  const [dateDurationStart, setDateDurationStart] = useState<Date | null>(null);
  const [dateDurationEnd, setDateDurationEnd] = useState<Date | null>(null);
  const [salesRepresentative, setSalesRepresentative] = useState<SalesRepresentative>(defaultSalesRepresentative);
  const [quotationStartParentNumber, setQuotationStartParentNumber] = useState<string>('');
  const [quotationStartChildNumber, setQuotationStartChildNumber] = useState<string>('');
  const [quotationEndParentNumber, setQuotationEndParentNumber] = useState<string>('');
  const [quotationEndChildNumber, setQuotationEndChildNumber] = useState<string>('');
  const [primaryStore, setPrimaryStore] = useState<PrimaryStore>(defaultPrimaryStore);
  const [secondaryStore, setSecondaryStore] = useState<SecondaryStore>(defaultSecondaryStore);
  const [isShowModalPrimaryStore, setShowModalPrimaryStore] = useState<boolean>(false);
  const [isShowModalSecondaryStore, setShowModalSecondaryStore] = useState<boolean>(false);
  const [isShowModalUser, setShowModalUser] = useState<boolean>(false);
  const { data: subInsLogData } = useSubscription(gql(insertedLogMaintenance));
  const [searchLogMaintenance, { data: logMData }] = useAppLazyQuery({
    query: gql(getLogMaintenance),
  });

  const getParamSearch = (): SearchInput => {
    return {
      esp_est_date: {
        from: dateDurationStart ? moment(dateDurationStart).format('YYYY-MM-DD') : null,
        to: dateDurationEnd ? moment(dateDurationEnd).format('YYYY-MM-DD') : null,
      },
      esp_est_nm: quoteName,
      esp_estima_full_no: {
        from: {
          child: parseInt(quotationStartChildNumber),
          parent: parseInt(quotationStartParentNumber),
        },
        to: {
          child: parseInt(quotationEndChildNumber),
          parent: parseInt(quotationEndParentNumber),
        },
      },
      esp_primary_cd: primaryStore?.mps_primary_cd === '' ? undefined : primaryStore?.mps_primary_cd,
      esp_secondary_cd: secondaryStore?.mss_secondary_cd === '' ? undefined : secondaryStore?.mss_secondary_cd,
      esp_syain_cd: salesRepresentative?.id,
    };
  };

  const renderTableData = useMemo(() => {
    if (quotationList?.length <= 0 || !quotationList) return;

    return quotationList?.map((quotation: QuotationListType, index: number) => {
      const estKb =
        parseInt(quotation?.esp_est_kb_no) < 10 ? `0${quotation?.esp_est_kb_no}` : `${quotation?.esp_est_kb_no}`;

      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td
            className="p-[8px] border-[1px] border-solid border-[#fff]"
            onClick={() => moveToQuotInput(quotation.esp_estima_no, quotation.esp_est_kb_no)}
          >
            <p className="cursor-pointer text-blue-600">{`${quotation?.esp_estima_no} - ${estKb}`}</p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <p>{quotation?.esp_est_nm}</p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <p>{quotation?.esp_est_date.split(' ')[0]}</p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <p>{quotation?.esp_primary_nm}</p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <p>{quotation?.esp_secondary_nm}</p>
          </td>
        </tr>
      );
    });
  }, [quotationList]);

  const renderTableHeader = () => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key}
      </th>
    ));
  };

  const setDataSalesRepresentative = (item: DataUser) => {
    const dataSalesRepresentative = {
      id: item?.mu_user_id,
      name: item?.mu_user_nm?.toString(),
    };
    setSalesRepresentative(dataSalesRepresentative);
  };

  const setDataPrimaryStore = (itemPrimaryStore: PrimaryStore) => {
    setPrimaryStore(itemPrimaryStore);
  };

  const setDataSecondaryStore = (itemSecondaryStore: SecondaryStore) => {
    setSecondaryStore(itemSecondaryStore);
  };

  const iconClose = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-4 h-4"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  };

  const searchComponent = () => {
    return (
      <div className="w-4/5 pt-[50px] pb-[50px] ">
        <div className="w-full flex-row flex justify-between pb-[10px]">
          <div className="w-2/5 items-center flex-row flex">
            <div className="flex bg-[#e2efd9] h-[50px] items-center justify-center p-[10px] w-1/5">
              <p>見積名</p>
            </div>
            <div className="ml-[20px] w-4/5">
              <input
                className={'h-[50px] border-[1px] border-solid border-[#000] w-full p-[10px]'}
                type="text"
                name="quoteName"
                onChange={(event) => {
                  if (event.target.value.length > 100) return;
                  setQuoteName(event?.target?.value);
                }}
                value={quoteName}
              />
            </div>
          </div>
          <div className="w-2/5 flex-row flex justify-between">
            <button
              onClick={() => onSearchQuoteList(getParamSearch())}
              className="flex bg-slate-400 h-[50px] items-center justify-center p-[10px] w-2/5"
            >
              <p>表示</p>
            </button>
            <button
              className="flex bg-slate-400 h-[50px] items-center justify-center p-[10px] w-2/5"
              onClick={() => moveToQuotInput()}
            >
              <p>新規</p>
            </button>
          </div>
        </div>
        <div className="w-full flex-row flex justify-between pb-[10px]">
          <div className="w-2/5 items-center flex-row flex">
            <div className="flex bg-[#e2efd9] h-[50px] items-center justify-center p-[10px] w-1/5">
              <p>期間</p>
            </div>
            <div className="w-4/5 ml-[20px] flex-row flex">
              <div className={'h-[50px] border-[1px] border-solid border-[#000] w-2/5 p-[5px] flex-row flex'}>
                <DatePicker
                  customInput={<input data-testid="date-start" />}
                  className={'w-full h-[40px] outline-0'}
                  selected={dateDurationStart}
                  onChange={(date: Date) => setDateDurationStart(date)}
                  dateFormat={'yyyy-MM-dd'}
                  locale="ja"
                />
                <button onClick={() => setDateDurationStart(null)} className={'w-1/5 h-[40px] outline-0'}>
                  {iconClose()}
                </button>
              </div>
              <div className="w-1/5 items-center justify-center flex">
                <p className="text-2xl">~</p>
              </div>
              <div className={'h-[50px] border-[1px] border-solid border-[#000] w-2/5 p-[5px] flex-row flex'}>
                <DatePicker
                  customInput={<input data-testid="date-end" />}
                  className={'w-full h-[40px] outline-0'}
                  selected={dateDurationEnd}
                  onChange={(date: Date) => setDateDurationEnd(date)}
                  dateFormat={'yyyy-MM-dd'}
                  locale="ja"
                />
                <button onClick={() => setDateDurationEnd(null)} className={'w-1/5 h-[40px] outline-0'}>
                  {iconClose()}
                </button>
              </div>
            </div>
          </div>
          <div className="w-2/5 flex-row flex">
            <div className="flex bg-[#e2efd9] h-[50px] items-center justify-center p-[10px] w-1/5">
              <p>営業担当者</p>
            </div>
            <div className="w-4/5 pl-[20px]">
              <div className={'h-[50px] border-[1px] border-solid border-[#000] w-full flex pr-[10px]'}>
                <input
                  data-testid="sales-representative-name"
                  onClick={() => setShowModalUser(true)}
                  className={'w-full p-[10px] focus:outline-none cursor-pointer'}
                  readOnly
                  value={salesRepresentative?.name || ''}
                />
                <button
                  onClick={() => setSalesRepresentative(defaultSalesRepresentative)}
                  className={'w-3/10 h-full items-center justify-center'}
                >
                  {iconClose()}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex-row flex justify-between pb-[10px]">
          <div className="w-2/5 items-center flex-row flex">
            <div className="flex bg-[#e2efd9] h-[50px] items-center justify-center p-[10px] w-1/5">
              <p>見積No.</p>
            </div>
            <div className="w-4/5 ml-[20px] flex-row flex">
              <input
                className={'h-[50px] border-[1px] border-solid border-[#000] w-2/5 p-[10px] outline-0'}
                type="number"
                name="quotationStartParentNumber"
                value={quotationStartParentNumber}
                onChange={(event) => {
                  if (event.target.value.length > 8) return;
                  setQuotationStartParentNumber(event?.target?.value);
                }}
                min={0}
                max={99999999}
              />
              <div className="w-1/5 items-center justify-center flex">
                <p className="text-2xl">-</p>
              </div>
              <input
                className={'h-[50px] border-[1px] border-solid border-[#000] w-2/5 p-[10px] outline-0'}
                type="number"
                value={quotationStartChildNumber}
                name="quotationStartChildNumber"
                onChange={(event) => {
                  if (event.target.value.length > 2) return;
                  setQuotationStartChildNumber(event?.target?.value);
                }}
                min={0}
                max={99}
              />
            </div>
          </div>
          <div className="w-2/5 flex-row flex">
            <div className="flex h-[50px] items-center justify-center p-[10px] w-1/5">
              <p className="text-2xl">~</p>
            </div>
            <div className="w-4/5 pl-[20px] flex-row flex">
              <input
                className={'h-[50px] border-[1px] border-solid border-[#000] w-2/5 p-[10px] outline-0'}
                type="number"
                name="quotationEndParentNumber"
                value={quotationEndParentNumber}
                onChange={(event) => {
                  if (event.target.value.length > 8) return;
                  setQuotationEndParentNumber(event?.target?.value);
                }}
                min={0}
                max={99999999}
              />
              <div className="w-1/5 items-center justify-center flex">
                <p className="text-2xl">-</p>
              </div>
              <input
                className={'h-[50px] border-[1px] border-solid border-[#000] w-2/5 p-[10px] outline-0'}
                type="number"
                name="quotationEndChildNumber"
                value={quotationEndChildNumber}
                onChange={(event) => {
                  if (event.target.value.length > 2) return;
                  setQuotationEndChildNumber(event?.target?.value);
                }}
                min={0}
                max={99}
              />
            </div>
          </div>
        </div>

        <div className="w-full flex-row flex justify-between pb-[10px]">
          <div className="w-2/5 items-center flex-row flex">
            <div className="flex bg-[#e2efd9] h-[50px] items-center justify-center p-[10px] w-1/5">
              <p>一次店</p>
            </div>
            <div className="ml-[20px] w-4/5 h-[50px] border-[1px] border-solid border-[#000] flex pr-[10px]">
              <button onClick={() => setShowModalPrimaryStore(true)} className={'h-full w-full p-[10px] flex'}>
                <p>{primaryStore?.mps_primary_nm}</p>
              </button>
              <button
                onClick={() => setPrimaryStore(defaultPrimaryStore)}
                className={'w-3/10 h-full items-center justify-center'}
              >
                {iconClose()}
              </button>
            </div>
          </div>
          <div className="w-2/5 flex-row flex">
            <div className="flex bg-[#e2efd9] h-[50px] items-center justify-center p-[10px] w-1/5">
              <p>二次店</p>
            </div>
            <div className="ml-[20px] w-4/5 h-[50px] border-[1px] border-solid border-[#000] flex pr-[10px] ">
              <button onClick={() => setShowModalSecondaryStore(true)} className={'h-full w-full p-[10px] flex'}>
                <p>{secondaryStore?.mss_secondary_nm}</p>
              </button>
              <button
                onClick={() => setSecondaryStore(defaultSecondaryStore)}
                className={'w-3/10 h-full items-center justify-center'}
              >
                {iconClose()}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const onSearchQuoteList = (param: Partial<SearchInput>) => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        searchQuotationList({ variables: { input: param } }).then((res) => {
          const quotList = res.data.getFilEstimaPointList;
          quotList.sort(
            (a: QuotationListType, b: QuotationListType) =>
              Number(`${a.esp_estima_no}${a.esp_est_kb_no}`) - Number(`${b.esp_estima_no}${b.esp_est_kb_no}`)
          );
          setQuotationList(res.data.getFilEstimaPointList);
        });
      })
      .catch((err) => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
        console.log(err);
      });
  };

  const moveToQuotInput = (no?: string, kbNo?: string) => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        navigate(`${pathURL.QUOTATION_INPUT.pathName}`, {
          state: {
            no: no ?? null,
            kb_no: kbNo ?? null,
          },
        });
      })
      .catch((err) => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
        console.log(err);
      });
  };

  useEffect(() => {
    if (!user) return;

    searchMstUser({ variables: { input: { mu_mail: !testEnv.isTestMode ? user?.username : testEnv.mail } } }).then(
      (res) => {
        const userData = res.data.getMstUser;
        if (userData.length <= 0) return;

        setSalesRepresentative({
          id: userData[0].mu_user_id,
          name: userData[0].mu_user_nm,
        });

        const paramSearch = {
          esp_est_date: {
            from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
            to: moment().format('YYYY-MM-DD'),
          },
          esp_syain_cd: userData[0].mu_user_id,
        };
        onSearchQuoteList(paramSearch);
      }
    );
  }, [user]);

  useEffect(() => {
    searchLogMaintenance();
  }, []);

  // 日付範囲の初期設定はMounting時に一度でよい。
  // Formatは、DatePickerのAttributeで設定される。
  useEffect(() => {
    setDateDurationStart(moment().subtract(30, 'days').toDate());
    setDateDurationEnd(moment().toDate());
  }, []);

  if (logMData?.getLogMaintenance.length > 0 || subInsLogData) return <Maintenance />;

  return (
    <MainLayout>
      <div className="justify-center items-center">
        <div className="flex flex-col justify-center items-center">
          {searchComponent()}
          <table className="text-center border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
            <tbody>
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader()}</tr>
              {!user || isMstUserLoading || isQuotationListLoading ? (
                <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                  <td
                    colSpan={header.length}
                    className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                renderTableData
              )}
            </tbody>
          </table>
        </div>
        <Dialog
          open={isShowModalPrimaryStore}
          onClose={() => setShowModalPrimaryStore(false)}
          className="relative z-50 w-full h-full"
        >
          <ModalGuidePrimaryStore setIsOpen={setShowModalPrimaryStore} onCallbackFunction={setDataPrimaryStore} />
        </Dialog>
        <Dialog
          open={isShowModalSecondaryStore}
          onClose={() => setShowModalSecondaryStore(false)}
          className="relative z-50 w-full h-full"
        >
          <ModalGuideSecondaryStore
            primaryStore={primaryStore}
            setIsOpen={setShowModalSecondaryStore}
            onCallbackFunction={setDataSecondaryStore}
          />
        </Dialog>
        <Dialog open={isShowModalUser} onClose={() => setShowModalUser(false)} className="relative z-50 w-full h-full">
          <ModalGuideUser setIsOpen={setShowModalUser} onCallbackFunction={setDataSalesRepresentative} />
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default QuotationList;
